'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '@/hooks/use-auth'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { Loader2, CheckCheck, Copy } from 'lucide-react'
import { toast } from 'sonner'
import { BILLING_NOTIFICATION_TYPES } from '@/lib/billing/constants'
import type { BillingNotification } from '@/lib/billing/types'

const SEVERITY_OPTIONS = ['critical', 'warning', 'info'] as const
const READ_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'unread', label: 'Unread' },
  { value: 'read', label: 'Read' },
] as const
const PAGE_SIZE = 50

/** Sentinel used in Select components — empty string is invalid for shadcn Select. */
const ANY = '__any__' as const

const TYPE_OPTIONS = Object.values(BILLING_NOTIFICATION_TYPES)

type Severity = (typeof SEVERITY_OPTIONS)[number]
type ReadFilter = (typeof READ_OPTIONS)[number]['value']

interface ListResponse {
  rows: BillingNotification[]
  totalCount: number
  limit: number
  offset: number
}

function severityVariant(severity: string): 'destructive' | 'secondary' | 'outline' {
  if (severity === 'critical') return 'destructive'
  if (severity === 'warning') return 'secondary'
  return 'outline'
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso)
  const now = new Date()
  const sameDay = d.toDateString() === now.toDateString()
  return sameDay
    ? d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleString(undefined, { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' })
}

async function copyTenantId(id: string) {
  try {
    await navigator.clipboard.writeText(id)
    toast.success('Tenant ID copied')
  } catch {
    toast.error('Failed to copy tenant ID')
  }
}

export function NotificationsTab() {
  const { fetchWithAuth } = useAuth()

  const [severity, setSeverity] = useState<Severity | typeof ANY>(ANY)
  const [type, setType] = useState<string>(ANY)
  const [tenantId, setTenantId] = useState<string>('')
  const [read, setRead] = useState<ReadFilter>('unread')
  const [offset, setOffset] = useState(0)

  const [rows, setRows] = useState<BillingNotification[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [bulkLoading, setBulkLoading] = useState(false)
  const [singleMarking, setSingleMarking] = useState<string | null>(null)

  /**
   * Monotonic counter: each `load()` claims a fresh sequence number, and a
   * response is only committed if its sequence is still the latest. Defends
   * against the operator changing a filter (or clicking Refresh) while a
   * previous request is in flight — without the guard, the slower request
   * can land last and clobber the correct results with stale ones.
   */
  const requestSeq = useRef(0)

  const buildQuery = useCallback(() => {
    const params = new URLSearchParams()
    if (severity !== ANY) params.set('severity', severity)
    if (type !== ANY) params.set('type', type)
    if (tenantId.trim()) params.set('tenantId', tenantId.trim())
    params.set('read', read)
    params.set('limit', String(PAGE_SIZE))
    params.set('offset', String(offset))
    return params.toString()
  }, [severity, type, tenantId, read, offset])

  const load = useCallback(async () => {
    const seq = ++requestSeq.current
    setLoading(true)
    setError(null)
    try {
      const res = await fetchWithAuth(`/api/admin/billing/notifications?${buildQuery()}`)
      if (seq !== requestSeq.current) return // a newer load() has started; drop this response
      if (!res.ok) {
        const err = await res.json().catch(() => ({} as { error?: string }))
        throw new Error(err.error || `HTTP ${res.status}`)
      }
      const data = (await res.json()) as ListResponse
      if (seq !== requestSeq.current) return // re-check after the second await
      setRows(data.rows)
      setTotalCount(data.totalCount)
      // Drop selections that are no longer in view to avoid stale bulk-select state.
      setSelected((prev) => {
        const visible = new Set(data.rows.map((r) => r.id))
        const next = new Set<string>()
        prev.forEach((id) => { if (visible.has(id)) next.add(id) })
        return next
      })
    } catch (err) {
      if (seq !== requestSeq.current) return
      setError(err instanceof Error ? err.message : 'Failed to load notifications')
    } finally {
      if (seq === requestSeq.current) setLoading(false)
    }
  }, [buildQuery, fetchWithAuth])

  useEffect(() => { void load() }, [load])

  // Filter setters reset offset atomically (same React event tick → one
  // re-render → one load) so the operator does not stay on a now-empty page
  // after narrowing the result set, and we never fire a `new-filter +
  // old-offset` request that the offset-reset effect then has to overwrite.
  const onSeverityChange = (next: Severity | typeof ANY) => {
    setSeverity(next)
    setOffset(0)
  }
  const onTypeChange = (next: string) => {
    setType(next)
    setOffset(0)
  }
  const onTenantIdChange = (next: string) => {
    setTenantId(next)
    setOffset(0)
  }
  const onReadChange = (next: ReadFilter) => {
    setRead(next)
    setOffset(0)
  }

  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const allUnreadVisibleIds = useMemo(
    () => rows.filter((r) => r.read_at === null).map((r) => r.id),
    [rows],
  )
  const allUnreadSelected =
    allUnreadVisibleIds.length > 0 && allUnreadVisibleIds.every((id) => selected.has(id))

  const toggleAllVisible = () => {
    if (allUnreadSelected) {
      setSelected(new Set())
    } else {
      setSelected(new Set(allUnreadVisibleIds))
    }
  }

  const markSingle = async (id: string) => {
    setSingleMarking(id)
    try {
      const res = await fetchWithAuth(`/api/admin/billing/notifications/${id}/read`, { method: 'PATCH' })
      if (!res.ok) {
        const err = await res.json().catch(() => ({} as { error?: string }))
        throw new Error(err.error || `HTTP ${res.status}`)
      }
      // Reload to keep totalCount + unread-filter view consistent (matches
      // the bulk-read path). A local patch would leave the row in place on
      // the unread filter and would not decrement `totalCount` shown in
      // pagination; reload is one extra round-trip but behaviour stays
      // correct without ad-hoc invalidation logic per filter mode.
      void load()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to mark read')
    } finally {
      setSingleMarking(null)
    }
  }

  const markBulk = async () => {
    const ids = Array.from(selected)
    if (ids.length === 0) return
    setBulkLoading(true)
    try {
      const res = await fetchWithAuth('/api/admin/billing/notifications/bulk-read', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({} as { error?: string }))
        throw new Error(err.error || `HTTP ${res.status}`)
      }
      const data = (await res.json()) as { updated: number }
      toast.success(`Marked ${data.updated} notification${data.updated === 1 ? '' : 's'} read`)
      setSelected(new Set())
      void load()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to bulk mark read')
    } finally {
      setBulkLoading(false)
    }
  }

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Severity</label>
              <Select value={severity} onValueChange={(v) => onSeverityChange(v as Severity | typeof ANY)}>
                <SelectTrigger className="h-8 w-32"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY}>All</SelectItem>
                  {SEVERITY_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Type</label>
              <Select value={type} onValueChange={onTypeChange}>
                <SelectTrigger className="h-8 w-56"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY}>All</SelectItem>
                  {TYPE_OPTIONS.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Tenant ID</label>
              <Input
                className="h-8 w-64 font-mono text-xs"
                placeholder="uuid (paste)"
                value={tenantId}
                onChange={(e) => onTenantIdChange(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Read state</label>
              <Select value={read} onValueChange={(v) => onReadChange(v as ReadFilter)}>
                <SelectTrigger className="h-8 w-32"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {READ_OPTIONS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" size="sm" className="h-8" onClick={() => void load()}>
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {selected.size > 0 && (
        <div className="flex items-center gap-3 rounded-md border bg-muted/40 px-3 py-2">
          <span className="text-sm">{selected.size} selected</span>
          <Button size="sm" onClick={markBulk} disabled={bulkLoading}>
            {bulkLoading && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
            <CheckCheck className="h-3.5 w-3.5 mr-1.5" />
            <span>Mark selected as read</span>
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Clear</Button>
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          {error && (
            <div className="p-4 text-sm text-destructive">{error}</div>
          )}
          {loading ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              No notifications matching filters.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8">
                    <Checkbox
                      checked={allUnreadSelected}
                      onCheckedChange={toggleAllVisible}
                      disabled={allUnreadVisibleIds.length === 0}
                      aria-label="Select all unread on this page"
                    />
                  </TableHead>
                  <TableHead className="w-28">Time</TableHead>
                  <TableHead className="w-24">Severity</TableHead>
                  <TableHead className="w-56">Type</TableHead>
                  <TableHead>Title / message</TableHead>
                  <TableHead className="w-44">Tenant</TableHead>
                  <TableHead className="w-32 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => {
                  const isUnread = row.read_at === null
                  const isSelected = selected.has(row.id)
                  return (
                    <TableRow key={row.id} className={isUnread ? '' : 'opacity-60'}>
                      <TableCell>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleOne(row.id)}
                          disabled={!isUnread}
                          aria-label={`Select ${row.id}`}
                        />
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                        {formatTimestamp(row.created_at)}
                      </TableCell>
                      <TableCell>
                        <Badge variant={severityVariant(row.severity)} className="text-xs">
                          {row.severity}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{row.type}</TableCell>
                      <TableCell>
                        <div className="text-sm font-medium">{row.title}</div>
                        <div className="text-xs text-muted-foreground line-clamp-2">{row.message}</div>
                      </TableCell>
                      <TableCell className="text-xs">
                        {row.tenant_id ? (
                          <div className="flex items-center gap-1">
                            <div className="flex flex-col min-w-0">
                              <span className="truncate" title={row.tenants?.name ?? '(unknown)'}>
                                {row.tenants?.name ?? <span className="italic text-muted-foreground">(unknown)</span>}
                              </span>
                              <span
                                className="font-mono text-[10px] text-muted-foreground truncate"
                                title={row.tenant_id}
                              >
                                {row.tenant_id.slice(0, 8)}…
                              </span>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 flex-shrink-0"
                              onClick={() => void copyTenantId(row.tenant_id!)}
                              title="Copy tenant UUID"
                              aria-label="Copy tenant UUID"
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                        ) : (
                          <span className="italic text-muted-foreground">system</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {isUnread ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={() => markSingle(row.id)}
                            disabled={singleMarking === row.id}
                          >
                            {singleMarking === row.id && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                            <span>Mark read</span>
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">read</span>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {totalCount} total · page {currentPage} of {totalPages}
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            disabled={offset === 0 || loading}
            onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            disabled={offset + PAGE_SIZE >= totalCount || loading}
            onClick={() => setOffset((o) => o + PAGE_SIZE)}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  )
}
